import { useState } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAdminVehicles, invalidateAdminVehicles, useAlert, useApi } from "../../hooks";
import { useLoaderStore } from "../../context";
import { API } from "../../utils/constants";
import { ActiveToggle, UseMyLocationButton } from "../../components/Inputs";

/**
 * formatTaxRate — Renders a decimal tax rate (e.g. 0.0875) as a percent string.
 * @param {number} rate
 * @returns {string} e.g. "8.75%"
 */
export function formatTaxRate(rate) {
  if (rate == null || isNaN(rate)) return "—";
  return `${(rate * 100).toFixed(2)}%`;
}

/** Returns true when a vehicle is Active (derived isActive, else status). */
function isVehicleActive(vehicle) {
  if (vehicle?.isActive != null) return vehicle.isActive;
  return vehicle?.status === "Active";
}

// ── Vehicle create/edit modal ───────────────────────────────────────────────

/**
 * VehicleModal — Create or edit a vehicle (CON-002 §8, §11).
 *
 * Create (no vehicle): POST /api/admin/vehicles (CreateVehicleRequest).
 * Edit (vehicle set):   PUT  /api/admin/vehicles/{id} (UpdateVehicleRequest).
 * Both accept `isActive` (server maps to DeactivatedAt). Tax rate is entered as
 * a percent for the admin and sent as a decimal.
 *
 * @param {object}        props
 * @param {boolean}       props.open
 * @param {Function}      props.onClose
 * @param {object|null}   props.vehicle - If set, edit mode; otherwise create.
 */
function VehicleModal({ open, onClose, vehicle = null }) {
  const isEdit = !!vehicle;
  const alert = useAlert();
  const { post, put } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [name, setName] = useState(vehicle?.name ?? "");
  const [active, setActive] = useState(isEdit ? isVehicleActive(vehicle) : true);
  const [address, setAddress] = useState(vehicle?.currentAddress ?? "");
  // Tax rate stored/displayed as a percent for the admin; sent as a decimal.
  const [taxPercent, setTaxPercent] = useState(
    vehicle?.taxRate != null ? (vehicle.taxRate * 100).toString() : ""
  );

  const validate = () => {
    if (!name.trim()) {
      alert("Validation", "Vehicle name is required.", "warning");
      return false;
    }
    const pct = parseFloat(taxPercent);
    if (taxPercent === "" || isNaN(pct) || pct < 0 || pct > 99.99) {
      alert("Validation", "Tax rate must be between 0% and 99.99%.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setShowLoader(true);

    const payload = {
      name: name.trim(),
      isActive: active,
      currentAddress: address.trim() || null,
      taxRate: Math.round(parseFloat(taxPercent) * 10000) / 1000000, // percent → decimal, 4dp
    };

    try {
      if (isEdit) {
        await put(API.ADMIN.VEHICLES.UPDATE(vehicle.id), payload);
        alert("Updated", `"${name}" updated.`, "success");
      } else {
        await post(API.ADMIN.VEHICLES.CREATE, payload);
        alert("Created", `"${name}" added.`, "success");
      }
      await invalidateAdminVehicles();
      onClose();
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to save vehicle.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const inputClass = "w-full border-2 border-charcoal rounded-md p-2 font-body text-ink";
  const labelClass = "font-heading tracking-wider text-teal text-sm block mb-1";

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6 my-8"
                data-testid="vehicle-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="font-display text-2xl text-charcoal uppercase">
                    {isEdit ? `EDIT ${vehicle?.name?.toUpperCase()}` : "ADD VEHICLE"}
                  </Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal" className="text-charcoal/40 hover:text-charcoal">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="vehicle-name" className={labelClass}>NAME *</label>
                    <input
                      id="vehicle-name" type="text" maxLength={150}
                      value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. The Silver Bullet"
                      className={inputClass} data-testid="vehicle-name-input"
                    />
                  </div>

                  {/* Active */}
                  <div className="flex items-center justify-between">
                    <label htmlFor="vehicle-active" className={labelClass + " mb-0"}>ACTIVE</label>
                    <ActiveToggle
                      enabled={active}
                      onToggle={() => setActive((a) => !a)}
                      label="Vehicle active"
                      testId="vehicle-active-toggle"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="vehicle-address" className={labelClass}>CURRENT ADDRESS</label>
                    <input
                      id="vehicle-address" type="text" maxLength={300}
                      value={address} onChange={(e) => setAddress(e.target.value)}
                      placeholder="e.g. 123 Nicollet Mall, Winston-Salem"
                      className={inputClass} data-testid="vehicle-address-input"
                    />
                    <UseMyLocationButton
                      onResolve={setAddress}
                      testId="vehicle-use-my-location"
                    />
                  </div>

                  {/* Tax rate */}
                  <div>
                    <label htmlFor="vehicle-tax" className={labelClass}>TAX RATE (%) *</label>
                    <input
                      id="vehicle-tax" type="number" min="0" max="99.99" step="0.01"
                      value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)}
                      placeholder="e.g. 8.75"
                      className={inputClass} data-testid="vehicle-tax-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="vehicle-submit-button"
                  >
                    {isEdit ? "SAVE" : "CREATE"}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Delete confirmation ─────────────────────────────────────────────────────

function DeleteVehicleDialog({ open, onClose, onConfirm, vehicleName }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
              data-testid="delete-vehicle-dialog"
            >
              <Dialog.Title className="font-display text-2xl text-charcoal uppercase mb-3">
                DELETE VEHICLE
              </Dialog.Title>
              <p className="font-body text-charcoal/70 mb-6">
                Delete <strong>"{vehicleName}"</strong>? This is permanent and cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onConfirm}
                  className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                  data-testid="confirm-delete-vehicle-button"
                >
                  DELETE
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Vehicle admin page ──────────────────────────────────────────────────────

/**
 * VehicleAdmin — Admin vehicle management (F-017, T-004, SPR-007).
 *
 * Lists vehicles with an Active toggle, address, and tax rate. The toggle PUTs
 * `isActive` immediately (CON-002 §9A). "Add Vehicle" POSTs a new vehicle and a
 * per-row delete permanently tombstones it. The edit modal updates all fields.
 */
export default function VehicleAdmin() {
  const alert = useAlert();
  const { put, del } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { vehicles, isLoading, error } = useAdminVehicles();

  const [modalOpen, setModalOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Remount key — blank-on-create / prefilled-on-edit / clear-on-success (FE-3).
  const [modalSession, setModalSession] = useState(0);

  const openCreate = () => { setEditVehicle(null); setModalSession((n) => n + 1); setModalOpen(true); };
  const openEdit = (v) => { setEditVehicle(v); setModalSession((n) => n + 1); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditVehicle(null); };

  const handleToggleActive = async (vehicle) => {
    const next = !isVehicleActive(vehicle);
    setShowLoader(true);
    try {
      await put(API.ADMIN.VEHICLES.UPDATE(vehicle.id), { isActive: next });
      await invalidateAdminVehicles();
      alert("Updated", `"${vehicle.name}" is now ${next ? "Active" : "Inactive"}.`, "success");
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to update status.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setShowLoader(true);
    try {
      await del(API.ADMIN.VEHICLES.DELETE(deleteTarget.id));
      await invalidateAdminVehicles();
      alert("Deleted", `"${deleteTarget.name}" permanently deleted.`, "success");
      setDeleteTarget(null);
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to delete vehicle.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="admin-vehicles-page" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-charcoal uppercase">Vehicle Management</h1>
          <p className="font-body text-charcoal/60 text-sm mt-1">
            Set each rig's status, current address, and tax rate.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid="add-vehicle-button"
        >
          + ADD VEHICLE
        </button>
      </div>

      {isLoading && <p className="font-body text-charcoal/60">Loading vehicles…</p>}
      {error && <p className="font-body text-rust">Unable to load vehicles.</p>}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-charcoal rounded-2xl overflow-hidden">
            <thead className="bg-charcoal text-white">
              <tr>
                {["Name", "Address", "Tax Rate", "Active", "Actions"].map((h) => (
                  <th key={h} className="font-heading tracking-wider text-left px-4 py-3 text-sm">
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vehicles.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-10 font-body text-charcoal/50">
                    No vehicles found.
                  </td>
                </tr>
              )}
              {vehicles.map((v) => (
                <tr
                  key={v.id}
                  className="border-b border-aluminum/40 hover:bg-cream/50 transition-colors"
                  data-testid={`vehicle-row-${v.id}`}
                >
                  <td className="px-4 py-3 font-body text-charcoal">{v.name}</td>
                  <td className="px-4 py-3 font-body text-charcoal/70 text-sm">
                    {v.currentAddress || <span className="italic text-charcoal/40">No address</span>}
                  </td>
                  <td className="px-4 py-3 font-body text-charcoal/70 text-sm" data-testid={`vehicle-tax-${v.id}`}>
                    {formatTaxRate(v.taxRate)}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveToggle
                      enabled={isVehicleActive(v)}
                      onToggle={() => handleToggleActive(v)}
                      label={`Toggle ${v.name} active`}
                      testId={`toggle-active-${v.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(v)}
                        className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors"
                        data-testid={`edit-vehicle-${v.id}`}
                      >
                        EDIT
                      </button>
                      <button
                        onClick={() => setDeleteTarget(v)}
                        className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1 rounded-full hover:bg-rust hover:text-white transition-colors"
                        data-testid={`delete-vehicle-${v.id}`}
                      >
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal — keyed so each open starts from fresh state */}
      <VehicleModal
        key={`${editVehicle ? `edit-${editVehicle.id}` : "create"}-${modalSession}`}
        open={modalOpen}
        onClose={closeModal}
        vehicle={editVehicle}
      />

      <DeleteVehicleDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        vehicleName={deleteTarget?.name ?? ""}
      />
    </main>
  );
}
